import { useNavigate } from "@tanstack/react-router";
import { ImageOffIcon } from "lucide-react";
import { useCallback, useId, useMemo, useState, type CSSProperties } from "react";

import { useUser } from "@/web/auth/useUser";
import { Button } from "@/web/components/primitives/button";
import {
  Drawer,
  DrawerBody,
  DrawerCloseIcon,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/web/components/primitives/drawer";
import { Input } from "@/web/components/primitives/input";
import { Label } from "@/web/components/primitives/label";
import { Spinner } from "@/web/components/primitives/spinner";
import { Switch } from "@/web/components/primitives/switch";
import { env } from "@/web/lib/env";
import { shareableCardUrl, shareBusinessCardLink, shareControlLabel } from "@/web/lib/shareCard";
import {
  type BusinessCategoryType,
  getFriendlyName,
} from "@/web/lib/schemaUtils/businessCategories";
import { useProcedureMutation, useProcedureQueryLazy } from "@/web/lib/tanstack";
import type { Uuid } from "spacetimedb";

import { procedures } from "@tycho/web-bindings";
import type {
  ShareableBusinessCardsV3,
  ShareBusinessCardContextRow,
} from "@tycho/web-bindings/types";
import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { useSelectedBrandIndex } from "@/web/routes/_protected/smb/-dashboard/brandSelection";
import { useSuspendedShareBusinessCardContexts } from "@/web/routes/_protected/smb/-dashboard/dashboardData";
import { useSuspendedSmbSuperView } from "@/web/routes/_protected/smb/-dashboard/superView";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";
import { TtwCard, type TtwCardPresentation } from "@/web/routes/ttw/-TtwCard";

/** Fallback gradient color (matches the original mock when no brand color is set). */
const FALLBACK_GRADIENT_COLOR = "#3c524f";

export type ShareBusinessCardProps = {
  underwritableEntity: Uuid;
  /**
   * Whether the owner has saved a TTW page yet. Switches this card between its
   * two stages:
   *
   *   false → the card IS their public presence. "Manage" opens the share
   *           drawer (link + password + browse visibility). This is the
   *           post-onboarding experience and is unchanged.
   *   true  → the PAGE is their public presence and the card is its plumbing.
   *           "Edit page" goes to the composer, which now owns those same
   *           settings on its Settings tab.
   *
   * See `docs/TTW_SHARE_UX_SPEC.md`.
   */
  hasTtwPage: boolean;
  /**
   * GCS objectPath of the saved page's hero image. When present it replaces the
   * brand hero as this card's background, so the card visibly wears the page's
   * own art rather than looking like a separate artifact.
   */
  ttwFeatureImage?: string;
};

// Sharing helpers now live in `lib/shareCard` so the public TTW page can use
// them without importing this module (and the whole dashboard tree with it).
// Re-exported because several dashboard surfaces already import them from here.
export { shareableCardUrl, shareBusinessCardLink };

function initialsFromEmail(email: string | undefined): string {
  if (!email) return "?";
  const local = email.split("@")[0] ?? email;
  if (local.length >= 2) return local.slice(0, 2).toUpperCase();
  return (local[0] ?? "?").toUpperCase();
}

/**
 * Builds an `image: linear-gradient(...)` value tied to a brand color so the
 * card's top wash matches the brand's primary color. We append two-char alpha
 * hex (8-digit hex format) instead of using Tailwind's `/95` syntax because the
 * color is dynamic and bundlers can't tree-shake arbitrary class strings.
 */
function brandGradientStyle(colorHex: string | undefined): CSSProperties {
  const color = (colorHex ?? FALLBACK_GRADIENT_COLOR).trim();
  // Only 6-char hex maps cleanly to alpha-suffixed 8-char hex; if anything else
  // shows up (rgb(...), shorthand #xyz, etc.) we still render a usable wash by
  // dropping alpha onto the raw value via `color-mix`.
  const isSixHex = /^#[0-9a-f]{6}$/i.test(color);
  if (isSixHex) {
    return {
      backgroundImage: `linear-gradient(to bottom, ${color}f2 0%, ${color}8c 50%, ${color}00 100%)`,
    };
  }
  return {
    backgroundImage: `linear-gradient(to bottom, color-mix(in srgb, ${color} 95%, transparent) 0%, color-mix(in srgb, ${color} 55%, transparent) 50%, transparent 100%)`,
  };
}

/**
 * Resolved presentational view of a `share_business_card_contexts` row,
 * pre-fallbacked so the card can render without conditional fluff.
 */
type ResolvedShareCard = {
  businessName: string;
  tagline: string;
  categoryLabel: string;
  locationLabel: string;
  /** `undefined` when the brand has no hero set yet — caller renders a `<HeroPlaceholder />` instead. */
  heroImageSrc: string | undefined;
  gradientColor: string | undefined;
};

function resolveShareCard(
  ctx: ShareBusinessCardContextRow | undefined,
  /**
   * Count of the entity's *active* targeted properties. Drives the tagline:
   * we only interpolate a street when the user is targeting exactly one
   * building; with zero or multiple we fall back to the generic copy.
   */
  activeTargetCount: number,
  /** Street line (`addressLine1`) of the sole active target, if exactly one. */
  soleTargetAddressLine1: string | undefined,
): ResolvedShareCard {
  const businessName = ctx?.brandFriendlyName ?? "Your business";
  // `heroCdnPath` is a CDN-relative key (e.g. `smb-onboarding/<user>/<id>-hero.jpg`),
  // not a URL — same convention as `BusinessCard.bgImage`. Compose it against
  // our imagekit base so the browser can actually fetch the bytes. When the
  // brand has no hero we leave this `undefined` and the renderer drops in a
  // `<HeroPlaceholder />` instead of pointing at a public/ asset.
  const heroImageSrc = ctx?.brandHeroCdnPath
    ? `${env.VITE_WITHCO_IMAGE_CDN_BASE}/${ctx.brandHeroCdnPath}`
    : undefined;
  const gradientColor = ctx?.brandColorPrimary ?? undefined;
  const categoryLabel = getFriendlyName(ctx?.brandBusinessType as BusinessCategoryType | undefined);

  const fallbackAddress = ctx?.brandBusinessAddress;
  const targetedAddress = ctx?.targetedPropertyAddress;
  const locationAddress = targetedAddress ?? fallbackAddress;
  const locationLabel = locationAddress
    ? [locationAddress.city, locationAddress.state].filter(Boolean).join(", ")
    : "";

  // Tagline rule: interpolate the street only when the user targets *exactly
  // one* property; with zero or multiple active targets we use the generic
  // "a building" (mirrors `TtwCard`). The count is sourced from the live
  // active targeted-properties list, not the context view's first-link-wins
  // `targetedPropertyAddress`, so it stays correct after add/remove. The
  // card's CSS handles wrapping — keeping this a single flowing string avoids
  // hard-coded line breaks that don't hold up across card widths.
  const tagline =
    activeTargetCount === 1 && soleTargetAddressLine1
      ? `Own ${soleTargetAddressLine1} with us`
      : "Own a building with us";

  return {
    businessName,
    tagline,
    categoryLabel,
    locationLabel,
    heroImageSrc,
    gradientColor,
  };
}

/**
 * Stand-in for the brand hero when `brandHeroCdnPath` isn't set yet. Uses the
 * same `bg-muted` + centered `ImageOffIcon` pattern as `SelectPropertyAction`'s
 * `<PropertyCard />` so a missing image reads as "no photo yet" instead of an
 * arbitrary stock fallback. Sits behind the brand-color gradient so the card
 * still picks up the brand tint when one is set.
 */
function HeroPlaceholder() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute -inset-6 z-0 flex items-center justify-center bg-muted text-muted-foreground"
    >
      <ImageOffIcon className="size-12" />
    </div>
  );
}

export function ShareBusinessCardDrawerBody({
  presentation,
  existingCard,
  onClose: _onClose,
  onComplete,
}: {
  /** Mapped from the `shareBusinessCardContexts` row so the drawer can render
   * the same `<TtwCard />` the recipient will see, just non-interactive. */
  presentation: TtwCardPresentation;
  /**
   * The entity's shareable card. Every SMB has one (minted at the end of
   * onboarding), so this drives the edit-in-place flow exclusively — there is
   * no "create" path here anymore. The form seeds from the saved access code
   * and browse visibility and mutates this row in-place.
   */
  existingCard: ShareableBusinessCardsV3;
  onClose: () => void;
  onComplete: () => void;
}) {
  const accessCodeSwitchId = useId();
  const accessCodeInputId = useId();
  const browsableSwitchId = useId();

  // Staged edits: seed from the saved card so re-opening reflects current
  // settings. "Confirm" commits all three at once; "Cancel" reverts to these
  // seeds. Tracked locally so the user can toggle freely before persisting.
  const savedRequireAccessCode = existingCard.accessCode !== undefined;
  const savedAccessCode = existingCard.accessCode ?? "";
  const [requireAccessCode, setRequireAccessCode] = useState(savedRequireAccessCode);
  const [accessCode, setAccessCode] = useState(savedAccessCode);
  const [browsable, setBrowsable] = useState(existingCard.browsable);

  const { mutateAsync: updateShareable, isPending: isUpdatingShareable } = useProcedureMutation(
    procedures.updateShareableBusinessCard,
  );

  // Live recipient view for the in-drawer preview. We re-run the same query
  // a `/ttw/<id>` visitor would land on so the count line under the static
  // CTA matches the real page once pledges start landing. We use the saved
  // `existingCard.accessCode ?? ""` (not the staged value) so the count is
  // always a faithful snapshot of what's currently published — the staged
  // password doesn't take effect until the user hits Confirm, and a typo
  // mid-edit would just bounce off the server with `[NOT_AUTHORIZED]` and
  // collapse the preview to the "Be the first to pledge" placeholder.
  //
  // While the query is in flight (first mount) or has errored, `data` is
  // `undefined` and the preview falls back to that placeholder. No error
  // boundary needed — the surrounding drawer controls stay interactive.
  const { data: recipientRow } = useProcedureQueryLazy(
    procedures.getShareableBusinessCard,
    { cardId: existingCard.id, accessCode: existingCard.accessCode ?? "" },
    {
      // Card is gated by its own access code; an unauthorized response is a
      // permanent miss, not a transient blip. Don't retry, don't refetch on
      // focus, and cache the row for a short while so re-opening the drawer
      // shows the latest count without a fresh round-trip.
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  );

  // Turning "Require password" on but leaving the field blank would silently
  // save a fully-public card — block the action until a code is entered.
  const missingRequiredAccessCode = requireAccessCode && accessCode.trim() === "";

  // Normalized staged access code (undefined === no password) for both the
  // dirty check and the mutation payload.
  const stagedAccessCode = requireAccessCode
    ? accessCode.trim() === ""
      ? undefined
      : accessCode.trim()
    : undefined;
  const isDirty =
    browsable !== existingCard.browsable || stagedAccessCode !== existingCard.accessCode;

  const reset = useCallback(() => {
    setRequireAccessCode(savedRequireAccessCode);
    setAccessCode(savedAccessCode);
    setBrowsable(existingCard.browsable);
  }, [savedRequireAccessCode, savedAccessCode, existingCard.browsable]);

  const confirm = useCallback(async () => {
    await updateShareable({
      cardId: existingCard.id,
      accessCode: stagedAccessCode,
      browsable,
    });
    onComplete();
  }, [updateShareable, existingCard.id, stagedAccessCode, browsable, onComplete]);

  // Sharing is decoupled from saving: the `/ttw/<id>` link always exists, so
  // this just opens the native share sheet / copies to clipboard.
  const share = useCallback(async () => {
    await shareBusinessCardLink(shareableCardUrl(existingCard.id));
  }, [existingCard.id]);

  return (
    <>
      <DrawerBody className="flex flex-col gap-6">
        <div className="flex justify-center">
          {/* Non-interactive preview of the recipient card so the SMB sees
              exactly what they're about to share. Capped narrower than the
              live page so it sits comfortably alongside the form controls. */}
          <TtwCard
            preview
            presentation={presentation}
            recipientRow={recipientRow}
            className="max-w-70"
          />
        </div>
        <Button color="blue" size="lg" onClick={async () => await share()}>
          {shareControlLabel()}
        </Button>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={browsableSwitchId} className="text-sm leading-snug font-medium">
            Show on public browse page
          </Label>
          <Switch
            id={browsableSwitchId}
            checked={browsable}
            onCheckedChange={setBrowsable}
            disabled={isUpdatingShareable}
            aria-label="Show on public browse page"
          />
        </div>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={accessCodeSwitchId} className="text-sm leading-snug font-medium">
            Require password
          </Label>
          <Switch
            id={accessCodeSwitchId}
            checked={requireAccessCode}
            onCheckedChange={(next) => {
              setRequireAccessCode(next);
              if (!next) setAccessCode("");
            }}
            disabled={isUpdatingShareable}
            aria-label="Require access code"
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label
            htmlFor={accessCodeInputId}
            className={requireAccessCode ? undefined : "text-muted-foreground"}
          >
            Password
          </Label>
          <Input
            id={accessCodeInputId}
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            disabled={!requireAccessCode || isUpdatingShareable}
            autoComplete="off"
          />
        </div>
      </DrawerBody>
      <DrawerFooter className="flex-row gap-2">
        <Button
          variant="outline"
          size="lg"
          className="flex-1"
          disabled={isUpdatingShareable || !isDirty}
          onClick={reset}
        >
          Cancel
        </Button>
        <Button
          color="yellow"
          size="lg"
          className="flex-1"
          disabled={isUpdatingShareable || !isDirty || missingRequiredAccessCode}
          onClick={async () => await confirm()}
        >
          {isUpdatingShareable ? (
            <span className="inline-flex items-center gap-2">
              <Spinner size="medium" />
              Saving…
            </span>
          ) : (
            "Confirm"
          )}
        </Button>
      </DrawerFooter>
    </>
  );
}

/**
 * Map a `shareBusinessCardContexts` row into the visual subset the recipient
 * card consumes. Both `ShareBusinessCardContextRow` and the recipient row
 * carry the same brand/property fields under matching keys so the cast is
 * just a re-pick — see `TtwCardPresentation` for the canonical shape.
 */
export function presentationFromContext(
  ctx: ShareBusinessCardContextRow | undefined,
): TtwCardPresentation {
  return {
    brandFriendlyName: ctx?.brandFriendlyName,
    brandHeroCdnPath: ctx?.brandHeroCdnPath,
    brandColorPrimary: ctx?.brandColorPrimary,
    brandBusinessType: ctx?.brandBusinessType,
    brandBusinessAddress: ctx?.brandBusinessAddress,
    targetedPropertyAddress: ctx?.targetedPropertyAddress,
  };
}

/**
 * The entity's live shared card (the row the "Update" flow edits), or
 * `undefined` if it hasn't been shared yet. `smbSuperView` doesn't filter
 * soft-deletes, so we skip deleted rows here — matching the server's
 * idempotency check in `createShareableBusinessCard`. Shared by every surface
 * that hosts the share drawer so they all resolve the same card.
 */
export function activeBusinessCard(
  businessCards: readonly { businessCard: ShareableBusinessCardsV3 }[],
): ShareableBusinessCardsV3 | undefined {
  return businessCards.map((bc) => bc.businessCard).find((bc) => bc.deletedAt === undefined);
}

function ShareBusinessCardAction({
  underwritableEntity,
  hasTtwPage,
  ttwFeatureImage,
  onSkip,
  onComplete,
}: ShareBusinessCardProps & SmbActionCoreProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const selectedBrandIndex = useSelectedBrandIndex();

  // Subscribe to the per-uw-entity contexts view and pick the row matching this
  // card's entity. Today we expect at most one, but if the view ever returns
  // duplicates (e.g. multi-brand membership during a migration) we just take
  // the first match — same default-to-first stance as the dashboard's
  // `defaultUwEntity` selection in `src/routes/_protected/profile/smb.tsx`.
  const shareContexts = useSuspendedShareBusinessCardContexts();
  // previous:
  // const shareContext = shareContexts.find((row) => row.underwritableEntity === underwritableEntity);
  const shareContext = shareContexts.find(
    (row) => row.underwritableEntity.compareTo(underwritableEntity) === 0,
  );
  // The active card stays in the deck even after a card is shared (the action
  // is `.always` and recycles), so look up the live card here too and hand it
  // to the edit drawer — same "update in place" path as the completed card.
  // Every SMB has a card from onboarding; `existingCard` is only ever undefined
  // for legacy users who onboarded before cards were minted automatically, in
  // which case the primary button mints one on demand (the drawer is
  // edit-only and never creates).
  const smbInfo = useSuspendedSmbSuperView(underwritableEntity);
  const existingCard = useMemo(
    () => activeBusinessCard(smbInfo.businessCards),
    [smbInfo.businessCards],
  );

  // Active targeted properties (soft-deletes already filtered out server-side)
  // drive the singular/plural tagline. We read the count + the sole address
  // here rather than from `shareContext.targetedPropertyAddress`, which is a
  // first-link-wins field that can't distinguish "one" from "many".
  const activeTargets = smbInfo.targetedProperties;
  const card = useMemo(
    () =>
      resolveShareCard(
        shareContext,
        activeTargets.length,
        activeTargets[0]?.targetableProperty.canonicalAddress.addressLine1,
      ),
    [shareContext, activeTargets],
  );
  const presentation = useMemo(() => presentationFromContext(shareContext), [shareContext]);

  const { mutateAsync: createShareable, isPending: isCreatingShareable } = useProcedureMutation(
    procedures.createShareableBusinessCard,
  );

  const onPrimary = useCallback(async () => {
    // Once a page exists, the composer owns everything this drawer used to —
    // including the password and browse-visibility settings, now on its Settings
    // tab. Sharing is reached from the composer preview's hero Share button.
    if (hasTtwPage) {
      // `?brand=` scopes the composer to the brand this deck is showing. Without
      // it a multi-brand owner's "Edit page" opened their FIRST brand's page —
      // and saving it wrote over that brand's content and card settings.
      void navigate({ to: "/profile/ttw-editor", search: { brand: selectedBrandIndex } });
      return;
    }
    if (existingCard) {
      setDrawerOpen(true);
      return;
    }
    // Legacy fallback: no card yet — mint one and share its link immediately.
    const { id } = await createShareable({ underwritableEntity, accessCode: undefined });
    await shareBusinessCardLink(shareableCardUrl(id));
    onComplete({ kind: "share", recycle: true, underwritableEntity });
  }, [
    hasTtwPage,
    navigate,
    selectedBrandIndex,
    existingCard,
    createShareable,
    underwritableEntity,
    onComplete,
  ]);

  const user = useUser();
  const initials = initialsFromEmail(user?.email);

  // The saved page's hero wins over the brand hero once a page exists — the
  // card should look like the thing it now opens.
  const backgroundSrc = useMemo(
    () =>
      (hasTtwPage && ttwFeatureImage
        ? `${env.VITE_WITHCO_IMAGE_CDN_BASE}/${ttwFeatureImage}`
        : undefined) ?? card.heroImageSrc,
    [hasTtwPage, ttwFeatureImage, card.heroImageSrc],
  );

  const cardSurfaceStyle = useMemo<CSSProperties | undefined>(
    () =>
      backgroundSrc
        ? {
            backgroundImage: `url(${backgroundSrc})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }
        : undefined,
    [backgroundSrc],
  );

  const gradientStyle = useMemo(() => brandGradientStyle(card.gradientColor), [card.gradientColor]);

  return (
    <>
      <ActionCard category="fundraise" hero style={cardSurfaceStyle}>
        {/* Bleed past the inner content wrapper so the wash reaches the bordered edge (`p-6` on the face). */}
        {backgroundSrc ? null : <HeroPlaceholder />}
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-6 z-0"
          style={gradientStyle}
        />
        <ActionCard.Main className="relative z-10 min-h-0 flex-1 gap-5 text-[#fafaf6]">
          <div className="flex flex-wrap gap-2">
            {card.categoryLabel ? (
              <span className="border border-white/90 bg-black/15 px-2 py-2 text-[11px] leading-none font-semibold tracking-wider uppercase backdrop-blur-[2px]">
                {card.categoryLabel}
              </span>
            ) : null}
            {card.locationLabel ? (
              <span className="border border-white/90 bg-black/15 px-2 py-2 text-[11px] leading-none font-semibold tracking-wider uppercase backdrop-blur-[2px]">
                {card.locationLabel}
              </span>
            ) : null}
          </div>
          {/* Pre-page the tagline pitches the ask ("Own 123 Main St with us");
              once a page exists the card represents that page, so it names it.
              Deliberately NOT "is live" — a password-protected page isn't, and
              this card has no access to that state. */}
          <p className="withus-strong text-[22px] leading-6.5">{card.tagline}</p>
          <div className="flex items-center gap-2">
            <div
              aria-hidden
              className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white text-[10px] font-semibold tracking-wide text-foreground uppercase"
            >
              {initials}
            </div>
            <p className="min-w-0 text-sm leading-snug font-semibold text-white">
              {card.businessName}
            </p>
          </div>
        </ActionCard.Main>
        <ActionCard.Footer className="relative z-10 mt-auto gap-2 pt-1">
          {/* `hero` + photo background means the palette's blue/blue button
              colors don't read; override to yellow-on-dark so the CTAs pop
              against the brand-tinted wash. */}
          <ActionCard.SkipButton
            color="yellow"
            className="border-withus-yellow bg-black/50 text-withus-yellow hover:bg-black/55"
            onSkip={onSkip}
          >
            Skip
          </ActionCard.SkipButton>
          <ActionCard.PrimaryButton
            color="yellow"
            className="w-auto! min-w-0 flex-1"
            disabled={isCreatingShareable}
            onClick={async () => await onPrimary()}
          >
            {hasTtwPage ? "Edit page" : existingCard ? "Manage & Share" : "Share"}
          </ActionCard.PrimaryButton>
        </ActionCard.Footer>
      </ActionCard>

      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Share with potential investors</DrawerTitle>
            <DrawerCloseIcon />
          </DrawerHeader>
          {existingCard ? (
            <ShareBusinessCardDrawerBody
              presentation={presentation}
              existingCard={existingCard}
              onClose={() => setDrawerOpen(false)}
              onComplete={() => {
                // Recycle to the back of the deck rather than dismiss — the
                // share action is repeatable and `.always` keeps it eligible,
                // so it should stay in the active stack after editing.
                onComplete({ kind: "share", recycle: true, underwritableEntity });
              }}
            />
          ) : null}
        </DrawerContent>
      </Drawer>
    </>
  );
}

export function createShareBusinessCardAction(
  props: ShareBusinessCardProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "share-business-card", canSkip = true, ...rest } = props;
  return {
    id,
    category: "fundraise",
    canSkip,
    render: (core) => <ShareBusinessCardAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import { CardRulesEngine } from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";
import { createShareBusinessCardCompleted } from "@/web/routes/_protected/smb/-dashboard/-completedActions/ShareBusinessCardCompleted";

// Every SMB has a card from onboarding, so the completed-side rule almost
// always wins (priority + `length > 0`); the active rule stays as a low-
// priority fallback for legacy users with no card yet, where its primary
// button mints one on demand.
export const shareBusinessCardRule = CardRulesEngine.singletonRule({
  id: "share-profile",
  priority: (d) => (d.shareableBusinessCards.length === 0 ? 110 : 10),
  when: CardRulesEngine.always,
  build: (d) =>
    createShareBusinessCardAction({
      underwritableEntity: d.underwritableEntityId,
      hasTtwPage: CardRulesEngine.hasTtwPage(d),
      // First live card's page art. Same first-wins resolution the rest of this
      // card uses; the composer is likewise single-card scoped.
      ttwFeatureImage: d.shareableBusinessCards.find((c) => c.ttwFeatureImage !== undefined)
        ?.ttwFeatureImage,
    }),
});

export const shareBusinessCardCompletedRule = CardRulesEngine.singletonCompletedRule({
  id: "share-profile:done",
  when: (d) => d.shareableBusinessCards.length > 0,
  build: (d) => createShareBusinessCardCompleted({ underwritableEntity: d.underwritableEntityId }),
});
